class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));
      if (!matches || matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().replace(/\s+/g, '-');
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // /th/search/suggest?section_id=... returns 417 (Shopify section rendering doesn't support locale-prefixed paths).
    // Non-default locales: use the .json endpoint (returns Thai translations) and build HTML client-side.
    // Default locale: use section rendering as normal.
    // Always use the JSON suggest endpoint so we can request variant SKU as a searchable field via
    // resources[options][fields] (title,…,variants.sku,variants.barcode). Shopify's DEFAULT predictive
    // fields EXCLUDE variant SKU, which is why SKU searches like "GBG25-195" returned nothing. This
    // works for the default locale AND locale-prefixed URLs; a 417 (some locale configs) falls back to
    // server-side section rendering. buildResultsMarkup renders the JSON exactly as before.
    const jsonUrl = `${routes.predictive_search_url}.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,article,query,collection&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,vendor`;
    fetch(jsonUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (response.status === 417) {
          // Locale-prefixed search AJAX unsupported on this store config — fall back to section rendering.
          return this.fetchWithSectionRendering(searchTerm, queryKey);
        }
        if (!response.ok) throw new Error(response.status);
        return response.json().then((data) => {
          const resultsMarkup = this.buildResultsMarkup(data, searchTerm);
          this.allPredictiveSearchInstances.forEach((inst) => { inst.cachedResults[queryKey] = resultsMarkup; });
          this.renderSearchResults(resultsMarkup);
        });
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  fetchWithSectionRendering(searchTerm, queryKey) {
    const baseUrl = routes.predictive_search_url.replace(/^\/[a-z]{2}(-[A-Z]{2})?\//i, '/');
    fetch(`${baseUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((inst) => { inst.cachedResults[queryKey] = resultsMarkup; });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  buildResultsMarkup(data, searchTerm) {
    const results = data.resources?.results || {};
    const alcoholKw = ['wine','champagne','prosecco','alcohol','spirits','liquor','whisky','whiskey','vodka','gin','rum','sake','soju','1818','beer','celebratory drinks'];
    const hasAlcohol = (str) => str && alcoholKw.some((kw) => str.toLowerCase().includes(kw));

    const queries = (results.queries || []).filter((q) => !hasAlcohol(q.text));
    const collections = (results.collections || []).filter((c) => !hasAlcohol(c.title) && !hasAlcohol(c.url));
    const suggestions = [...queries, ...collections];
    const products = (results.products || []).filter(
      (p) => !hasAlcohol(p.title) && !hasAlcohol(p.product_type) && !(p.tags || []).some((t) => hasAlcohol(t))
    );
    const allPages = [...(results.pages || []), ...(results.articles || [])];

    const t = {
      suggestions: this.dataset.tSuggestions || 'Suggestions',
      products: this.dataset.tProducts || 'Products',
      pages: this.dataset.tPages || 'Pages',
      searchFor: (this.dataset.tSearchFor || `Search for "__TERMS__"`).replace('__TERMS__', searchTerm),
    };
    const esc = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const fmtPrice = (p) => parseFloat(p).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' THB';

    const hasFirstColumn = suggestions.length > 0 || allPages.length > 0;
    const hasProducts = products.length > 0;
    const totalResults = suggestions.length + products.length + allPages.length;

    let html = '';

    if (totalResults > 0) {
      const cls = [
        'predictive-search__results-groups-wrapper',
        hasProducts ? '' : 'predictive-search__results-groups-wrapper--no-products',
        suggestions.length > 0 ? '' : 'predictive-search__results-groups-wrapper--no-suggestions',
      ].filter(Boolean).join(' ');

      html += `<div id="predictive-search-results" role="listbox"><div id="predictive-search-results-groups-wrapper" class="${cls}">`;

      if (hasFirstColumn) {
        html += `<div class="predictive-search__result-group">`;

        if (suggestions.length > 0) {
          html += `<div><h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(t.suggestions)}</h2>`;
          html += `<ul id="predictive-search-results-queries-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-queries">`;
          suggestions.forEach((item, i) => {
            const label = item.text || item.title || '';
            html += `<li id="predictive-search-option-query-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
            html += `<a href="${esc(item.url)}" class="predictive-search__item link link--text" tabindex="-1">`;
            html += `<div class="predictive-search__item-content predictive-search__item-content--centered">`;
            html += `<p class="predictive-search__item-heading predictive-search__item-query-result h5" aria-label="${esc(label)}">${item.styled_text || esc(label)}</p>`;
            html += `</div></a></li>`;
          });
          html += `</ul></div>`;
        }

        if (allPages.length > 0) {
          html += `<div class="predictive-search__pages-wrapper">`;
          html += `<h2 id="predictive-search-pages-desktop" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(t.pages)}</h2>`;
          html += `<ul id="predictive-search-results-pages-list-desktop" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-pages-desktop">`;
          allPages.forEach((page, i) => {
            html += `<li id="predictive-search-option-page-desktop-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
            html += `<a href="${esc(page.url)}" class="predictive-search__item link link--text" tabindex="-1">`;
            html += `<div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading h5">${esc(page.title)}</p></div>`;
            html += `</a></li>`;
          });
          html += `</ul></div>`;
        }

        html += `</div>`;
      }

      if (hasProducts || allPages.length > 0) {
        html += `<div class="predictive-search__result-group">`;

        if (hasProducts) {
          html += `<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(t.products)}</h2>`;
          html += `<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">`;
          products.forEach((product, i) => {
            const imgSrc = product.featured_image?.url
              ? product.featured_image.url + (product.featured_image.url.includes('?') ? '&' : '?') + 'width=150'
              : '';
            html += `<li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
            html += `<a href="${esc(product.url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">`;
            if (imgSrc) html += `<img class="predictive-search__image" src="${esc(imgSrc)}" alt="${esc(product.featured_image?.alt || product.title)}" width="50" height="50">`;
            html += `<div class="predictive-search__item-content"><p class="predictive-search__item-heading h5">${esc(product.title)}</p>`;
            html += `<div class="price-item price-item--regular">${fmtPrice(product.price)}</div></div></a></li>`;
          });
          html += `</ul></div>`;
        }

        if (allPages.length > 0) {
          html += `<div class="predictive-search__pages-wrapper">`;
          html += `<h2 id="predictive-search-pages-mobile" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(t.pages)}</h2>`;
          html += `<ul id="predictive-search-results-pages-list-mobile" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-pages-mobile">`;
          allPages.forEach((page, i) => {
            html += `<li id="predictive-search-option-page-mobile-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">`;
            html += `<a href="${esc(page.url)}" class="predictive-search__item link link--text" tabindex="-1">`;
            html += `<div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading h5">${esc(page.title)}</p></div>`;
            html += `</a></li>`;
          });
          html += `</ul></div>`;
        }

        html += `</div>`;
      }

      html += `</div></div>`;
    }

    html += `<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">`;
    html += `<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">`;
    html += `<span data-predictive-search-search-for-text>${esc(t.searchFor)}</span>`;
    html += `<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-arrow" viewBox="0 0 14 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"/></svg></span>`;
    html += `</button></div>`;
    html += `<span class="hidden" data-predictive-search-live-region-count-value>${totalResults}</span>`;

    return html;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // For section rendering (default locale), patch the 4 UI label strings with correct locale translations.
    // For JSON mode (non-default locale), labels are already correct — the replace calls below are safe no-ops.
    const t = {
      suggestions: this.dataset.tSuggestions,
      products: this.dataset.tProducts,
      pages: this.dataset.tPages,
      searchFor: this.dataset.tSearchFor,
    };
    if (t.suggestions) {
      this.predictiveSearchResults.querySelector('#predictive-search-queries')?.replaceChildren(
        document.createTextNode(t.suggestions)
      );
    }
    if (t.products) {
      this.predictiveSearchResults.querySelector('#predictive-search-products')?.replaceChildren(
        document.createTextNode(t.products)
      );
    }
    if (t.pages) {
      for (const el of this.predictiveSearchResults.querySelectorAll(
        '#predictive-search-pages-desktop, #predictive-search-pages-mobile'
      )) {
        el.replaceChildren(document.createTextNode(t.pages));
      }
    }
    if (t.searchFor && t.searchFor.includes('__TERMS__')) {
      const searchForEl = this.predictiveSearchResults.querySelector('[data-predictive-search-search-for-text]');
      if (searchForEl) {
        searchForEl.textContent = t.searchFor.replace('__TERMS__', this.searchTerm);
      }
    }

    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
